import { useMemo, useCallback, useRef, useEffect } from 'react';
import type { GridApi, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';

import Api from '@/domain/api';
import { showApiErrorToast } from '@/services/api';
import type { FilterOption } from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/Filters/useFieldProps';
import type {
  PeriodValue,
  TopVendorValue,
} from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/Filters/options';

export type VendorExpensesFilters = {
  period: PeriodValue;
  topVendors: TopVendorValue;
  currencies: FilterOption[];
  vendors: FilterOption[];
};

export type VendorExpenseRowMeta = {
  isFirst: boolean;
  isLast: boolean;
  isOnly: boolean;
  index: number;
};

export type VendorExpenseRow = {
  vendor_id: string | number;
  currency?: string;
  underlings?: VendorExpenseRow[];
  __meta?: VendorExpenseRowMeta;
} & Record<string, unknown>;

const useServerDatasource = (filters: VendorExpensesFilters): IServerSideDatasource<VendorExpenseRow> => {
  const filtersRef = useRef<VendorExpensesFilters>(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const prepareUnderlingsRowDataWithMeta = useCallback(
    (data: VendorExpenseRow[]): VendorExpenseRow[] =>
      data.map((item, index, array) => ({
        ...item,
        __meta: {
          isFirst: index === 0,
          isLast: index === array.length - 1,
          isOnly: array.length === 1,
          index,
        },
      })),
    [],
  );

  const recursivelyPopulateHierarchy = useCallback(
    (api: GridApi<VendorExpenseRow>, route: string[], node: VendorExpenseRow) => {
      if (node.underlings) {
        const underlingsWithPositions = prepareUnderlingsRowDataWithMeta(node.underlings);
        api.applyServerSideRowData({
          route,
          successParams: {
            rowData: underlingsWithPositions,
            rowCount: underlingsWithPositions.length,
          },
        });
      }
    },
    [prepareUnderlingsRowDataWithMeta],
  );

  return useMemo<IServerSideDatasource<VendorExpenseRow>>(
    () => ({
      getRows: async (params: IServerSideGetRowsParams<VendorExpenseRow>) => {
        const { success, request, api } = params;

        if (request.groupKeys.length) {
          params.fail();
          return;
        }

        try {
          const currentFilters = filtersRef.current;

          const {
            data: { items, count },
          } = await Api.getDashboardVendorExpensesRows({
            data: {
              period: currentFilters.period,
              currencies: currentFilters.currencies.map((currency) => currency.id),
              vendors: currentFilters.vendors.map((vendor) => vendor.id),
              topVendors: currentFilters.topVendors,
            },
          });

          const rows = items as VendorExpenseRow[];

          success({ rowData: rows, rowCount: count });

          rows.forEach((topLevelNode) => {
            recursivelyPopulateHierarchy(api, [String(topLevelNode.vendor_id)], topLevelNode);
          });
        } catch (error) {
          console.error('[ERROR]: on fetch grid rows', error);

          showApiErrorToast(error);

          success({ rowData: [], rowCount: 0 });
        }
      },
    }),
    [recursivelyPopulateHierarchy],
  );
};

export default useServerDatasource;
